import { Controller } from "@hotwired/stimulus"

function escHtml(str) {
  const div = document.createElement("div")
  div.textContent = String(str ?? "")
  return div.innerHTML
}

export default class extends Controller {
  static values = {
    plants: { type: Array, default: [] },
    showInactive: { type: Boolean, default: false }
  }

  async connect() {
    this.L = await import("leaflet")

    delete this.L.Icon.Default.prototype._getIconUrl
    this.L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png"
    })

    // Defer map creation until the container has non-zero dimensions.
    if (this.element.clientWidth > 0 && this.element.clientHeight > 0) {
      this._initMap()
    } else {
      this._waitForSize()
    }
  }

  disconnect() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this.map) this.map.remove()
  }

  _waitForSize() {
    this._resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      if (width > 0 && height > 0) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
        this._initMap()
      }
    })
    this._resizeObserver.observe(this.element)
  }

  _initMap() {
    const L = this.L

    this.map = L.map(this.element).setView([20.5937, 78.9629], 5)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(this.map)

    this.markers = []
    this.addMarkers()

    // Watch for further resizes (e.g. sidebar toggle).
    // invalidateSize alone doesn't reposition already-loaded tiles, so we
    // also reset the view to force a full tile re-render.
    this._resizeObserver = new ResizeObserver(() => {
      if (!this.map) return
      this.map.invalidateSize()
      this.map.setView(this.map.getCenter(), this.map.getZoom())
    })
    this._resizeObserver.observe(this.element)
  }

  addMarkers() {
    const L = this.L
    const bounds = []

    this.markers.forEach(m => this.map.removeLayer(m))
    this.markers = []

    this.plantsValue.forEach(plant => {
      if (!plant.lat || !plant.lng) return

      const color = this.markerColor(plant)
      const icon = L.divIcon({
        className: "custom-marker",
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="34" viewBox="0 0 24 30"
                    style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 18 12 18s12-9 12-18c0-6.63-5.37-12-12-12z"
                fill="${color}" stroke="white" stroke-width="2"/>
          <circle cx="12" cy="12" r="4.5" fill="white"/>
        </svg>`,
        iconSize: [28, 34],
        iconAnchor: [14, 32],
        popupAnchor: [0, -30]
      })

      const marker = L.marker([plant.lat, plant.lng], { icon })
        .addTo(this.map)
        .bindPopup(this.popupContent(plant))

      if (plant.radius) {
        L.circle([plant.lat, plant.lng], {
          radius: plant.radius,
          color: "#8290a0",
          fillColor: "#8290a0",
          fillOpacity: 0.05,
          weight: 1
        }).addTo(this.map)
      }

      this.markers.push(marker)
      bounds.push([plant.lat, plant.lng])
    })

    if (bounds.length > 0) {
      this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 })
    } else if (this.plantsValue.length > 0) {
      this._showNoCoordsNotice()
    }
  }

  _showNoCoordsNotice() {
    if (this._noticeShown) return
    this._noticeShown = true
    const notice = document.createElement("div")
    notice.style.cssText =
      "position:absolute;top:12px;left:50%;transform:translateX(-50%);" +
      "z-index:1000;padding:8px 14px;border-radius:6px;" +
      "background:#fff7ed;color:#9a3412;border:1px solid #fdba74;" +
      "font-size:12px;box-shadow:0 1px 3px rgba(0,0,0,0.15);"
    notice.textContent =
      "No plant locations set. Configure geo-fences from each plant's detail page to see them on the map."
    this.element.style.position = "relative"
    this.element.appendChild(notice)
  }

  markerColor(plant) {
    if (plant.status === "inactive") return "#9ca3af"
    return "#809888"
  }

  popupContent(plant) {
    return `
      <div style="min-width: 200px;">
        <strong style="font-size: 14px;">${escHtml(plant.name)}</strong><br>
        <span style="color: #666; font-size: 12px;">${escHtml(plant.plant_type)} &mdash; ${escHtml(plant.capacity_kld || 0)} KLD</span>
        <hr style="margin: 6px 0;">
        <span style="font-size: 12px; color: #888;">Status: ${escHtml(plant.status)}</span>
        <br><br>
        <a href="${escHtml(plant.path)}" style="color: #8290a0; font-size: 12px; text-decoration: underline;">View Details &rarr;</a>
      </div>
    `
  }

  toggleInactive(event) {
    this.showInactiveValue = event.target.checked
    this.addMarkers()
  }
}
